import React from 'react'
import { Link } from 'react-router-dom'
import { ToolkitPageLayout } from '../../../components'
import {
  TreatmentPlanInfusionCenterImage,
  TreatmentPlanOutpatientSCDImage,
  TreatmentPlanEDImage
} from 'assets'

// Component to render PDF download links, styled like toolkit buttons
const PdfDownloadLink: React.FC<{
  href: string
  label: string
  ariaLabel?: string
}> = ({ href, label, ariaLabel }) => (
  <div>
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      download
      aria-label={ariaLabel || `Download ${label}`}
      className="bg-[#142841] hover:bg-[#0F1E30] text-white rounded-md px-4 py-2 text-sm font-medium transition-colors duration-200 inline-flex items-center w-full justify-center text-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-5 w-5 mr-2 flex-shrink-0"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
          clipRule="evenodd"
        />
      </svg>
      <span className="truncate">{label}</span>
    </a>
  </div>
)

export const ProvidersTreatmentPlansPage: React.FC = () => {
  // TODO: Get content from plans/toolkit_content.json for "Patient Specific Treatment Plans" (Providers section)
  // TODO: Convert HTML to JSX
  // TODO: Import and use images

  return (
    <ToolkitPageLayout
      title="Patient Specific Treatment Plans"
      breadcrumbs={[
        { label: 'Toolkit', path: '/toolkit' },
        { label: 'Providers', path: '/toolkit/providers' },
        {
          label: 'Patient Specific Treatment Plans',
          path: '/toolkit/providers/treatment-plans'
        }
      ]}
    >
      <div className="prose max-w-none text-[#3C2416]">
        <blockquote className="border-l-4 border-[#A6410A] pl-4 italic my-8">
          <p>
            The goal for you is to use these sample templates to assist with
            managing persons living with sickle cell disease in the infusion
            center, outpatient and emergency department settings.
          </p>
        </blockquote>

        <hr className="my-8" />

        <p className="text-xl font-semibold mb-4">
          Review the sample templates below and modify them to your clinic
          needs:
        </p>

        <div className="grid md:grid-cols-3 gap-8 mb-8 items-start">
          <div className="text-center">
            <p className="text-lg font-medium mb-2">
              Sickle Cell Infusion Center Treatment Template
            </p>
            <a
              href="https://docs.google.com/document/d/e/2PACX-1vQcZoJ8zmmzKSt1fYM7F814NU_Ge2Od8OCtiBdNe0CvVJ4w1aAZv0iF50Mrsa7dkA/pub"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block border border-gray-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <img
                src={TreatmentPlanInfusionCenterImage}
                alt="Sickle Cell Infusion Center Treatment Template Preview"
                className="max-w-full h-auto rounded-lg shadow-md"
              />
            </a>
          </div>
          <div className="text-center">
            <p className="text-lg font-medium mb-2">
              Outpatient Sickle Cell Disease Template
            </p>
            <a
              href="https://docs.google.com/document/d/e/2PACX-1vTkQhobbu7G3nHMY_8mAJuhrGv48ybi1AvGyzJitybrW1ahyRAMb5tWMfJbzAjCJQ/pub"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block border border-gray-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <img
                src={TreatmentPlanOutpatientSCDImage}
                alt="Outpatient Sickle Cell Disease Template Preview"
                className="max-w-full h-auto rounded-lg shadow-md"
              />
            </a>
          </div>
          <div className="text-center">
            <p className="text-lg font-medium mb-2">
              Emergency Department Treatment Plan Template
            </p>
            <a
              href="https://docs.google.com/document/d/e/2PACX-1vQz9D2Lzm9shHOF6sGmI8Kc0HKQ0VLfIqVacL-DcvzlsZriR0ruEnGwwmzOXTlX2Q/pub"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block border border-gray-300 rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-200"
            >
              <img
                src={TreatmentPlanEDImage}
                alt="Emergency Department Treatment Plan Template Preview"
                className="max-w-full h-auto rounded-lg shadow-md"
              />
            </a>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-center mt-12 mb-6 text-[#142841]">
          Telemedicine
        </h2>
        <div className="flex justify-center mb-8">
          <div className="w-full max-w-xs">
            <PdfDownloadLink
              href="https://www.dropbox.com/scl/fi/17mv1eyjq4o2fl6lfxsoh/Telemedicine-Note-Template.pdf?rlkey=kj3n74kqceh7i8ql6iwq3lsyv&dl=0&dl=1"
              label="Telemedicine Note Template"
            />
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-center mt-12 mb-6 text-[#142841]">
          Medication and Blood Therapy Plans
        </h2>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-6 max-w-2xl mx-auto mb-8">
          <PdfDownloadLink
            href="https://www.dropbox.com/scl/fi/dyt2gn2ko1xibahbyn3kc/Crizanlizumab-Therapy-Plan.pdf?rlkey=6m3cjywrmhumvey0vhm75t9qe&dl=0&dl=1"
            label="Crizanlizumab Therapy Plan"
          />
          <PdfDownloadLink
            href="https://www.dropbox.com/scl/fi/oxoxgm3u2y3d61oa44rk3/Epo-Injections.pdf?rlkey=lv62t3n7kut7snho8ipr0fdbj&dl=0&dl=1"
            label="Erythropoietin Injection Therapy Plan"
          />
          <PdfDownloadLink
            href="https://www.dropbox.com/scl/fi/9pq09o5bn09v1bzn1guwu/Partial-Exchanges-and-Simple-Transfusions.pdf?rlkey=jgwgczglvwlxixom5cyksdeex&dl=0&dl=1"
            label="Partial Exchanges and Simple Transfusions"
          />
          <PdfDownloadLink
            href="https://www.dropbox.com/scl/fi/xaryvag7vj0z7oi2fxgri/Red-Cell-Exchanges.pdf?rlkey=tjc21r58bz1bzwsgi4av39gf9&dl=0&dl=1"
            label="Red Cell Exchanges"
          />
        </div>

        <h2 className="text-2xl font-semibold text-center mt-12 mb-6 text-[#142841]">
          Medication Ordersets
        </h2>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-6 max-w-2xl mx-auto mb-8">
          <PdfDownloadLink
            href="https://www.dropbox.com/scl/fi/in06acwrao9dnb0iqxzyv/PCA-Pumps.pdf?rlkey=t2pezhtf7jx2f4970ybadow7r&dl=0&dl=1"
            label="PCA Pumps"
          />
          <PdfDownloadLink
            href="https://www.dropbox.com/scl/fi/2e38bv6nx8speavfoh9b3/Best-Practice-Advisory-for-Labs.pdf?rlkey=k1icptnr48yz6nzeiznsnl7c0&dl=0&dl=1"
            label="Best Practice Advisory for Labs"
          />
        </div>

        <hr className="my-12" />

        <div className="mt-12 mb-8 text-center">
          <Link
            to="/toolkit/providers"
            className="bg-[#A6410A] hover:bg-[#894d35] text-white rounded-md px-6 py-3 text-lg font-medium transition-colors duration-200 shadow-md hover:shadow-lg"
          >
            All done? Click here to return to the Providers screen
          </Link>
        </div>
      </div>
    </ToolkitPageLayout>
  )
}
